import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { formatApiErrorDetail } from '@/lib/utils';
import logger from '@/lib/logger';
import axios from 'axios';
import { Lightning, ArrowLeft, Check } from '@phosphor-icons/react';
import { LoginForm, RegisterForm, ForgotForm, ResetForm } from '@/components/auth/AuthForms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBMIT_LABELS = { login: 'Sign In', register: 'Create Account', forgot: 'Send Reset Token', reset: 'Reset Password' };
const HEADINGS = {
  login: { title: 'WELCOME BACK', sub: 'Sign in to continue your training' },
  register: { title: 'JOIN THE MOVEMENT', sub: 'Create your account and start training' },
  forgot: { title: 'RESET PASSWORD', sub: 'Enter your email to receive a reset token' },
  reset: { title: 'NEW PASSWORD', sub: 'Enter your new password below' },
};

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-apply referral code from ?ref= URL param → pre-fill + switch to register
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.trim().toUpperCase());
      setMode('register');
    }
  }, [searchParams]);

  const switchMode = (newMode) => { setMode(newMode); setError(''); setSuccess(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else if (mode === 'register') {
        await register(email, password, name);
        navigate('/dashboard');
      } else if (mode === 'forgot') {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        if (data.reset_token) {
          setResetToken(data.reset_token);
          setMode('reset');
          setSuccess('Reset token generated! Enter your new password below.');
        } else {
          setSuccess(data.message || 'If that email exists, a reset link has been sent.');
        }
      } else if (mode === 'reset') {
        await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
        setSuccess('Password reset successfully! You can now sign in.');
        setTimeout(() => switchMode('login'), 2000);
      }
    } catch (err) {
      logger.error('Auth error:', err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const heading = HEADINGS[mode];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="auth-page">
      {/* Left - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1758599878868-52cced2f8154?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBib2R5d2VpZ2h0JTIwd29ya291dCUyMGhvbWV8ZW58MHx8fHwxNzc1OTQ2MDA0fDA&ixlib=rb-4.1.0&q=85" alt="Workout" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="text-5xl font-black tracking-tighter text-white mb-4">YOUR BODY.<br />YOUR GYM.</h2>
          <p className="text-[#A1A1AA] text-lg max-w-md">No equipment. No excuses. Just results.</p>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Lightning weight="fill" className="text-[#FF3B30] w-8 h-8" />
            <span className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight text-white">GRIT.FIT</span>
          </Link>

          {mode === 'forgot' && (
            <button onClick={() => switchMode('login')} className="flex items-center gap-1 text-[#A1A1AA] hover:text-white mb-4 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          )}

          <h1 className="text-4xl font-black tracking-tighter mb-2">{heading.title}</h1>
          <p className="text-[#A1A1AA] mb-8">{heading.sub}</p>

          {error && <div data-testid="auth-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 mb-6 text-sm">{error}</div>}
          {success && <div data-testid="auth-success" className="bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] px-4 py-3 mb-6 text-sm flex items-center gap-2"><Check weight="bold" className="w-4 h-4" /> {success}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'login' && <LoginForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} showPassword={showPassword} setShowPassword={setShowPassword} />}
            {mode === 'register' && <RegisterForm name={name} setName={setName} email={email} setEmail={setEmail} password={password} setPassword={setPassword} showPassword={showPassword} setShowPassword={setShowPassword} referralCode={referralCode} setReferralCode={setReferralCode} />}
            {mode === 'forgot' && <ForgotForm email={email} setEmail={setEmail} />}
            {mode === 'reset' && <ResetForm newPassword={newPassword} setNewPassword={setNewPassword} />}

            <button data-testid="auth-submit-button" type="submit" disabled={loading} className="btn-primary w-full py-3.5 text-lg disabled:opacity-50">
              {loading ? 'Loading...' : SUBMIT_LABELS[mode]}
            </button>

            {mode === 'register' && (
              <p className="text-xs text-[#71717A] text-center" data-testid="auth-legal-notice">
                By creating an account you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] underline hover:text-white" data-testid="auth-terms-link">Terms</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] underline hover:text-white" data-testid="auth-privacy-link">Privacy Policy</a>.
              </p>
            )}
          </form>

          {mode === 'login' && (
            <button onClick={() => switchMode('forgot')} className="text-[#71717A] hover:text-[#A1A1AA] text-sm mt-4 block" data-testid="forgot-password-link">
              Forgot your password?
            </button>
          )}

          <p className="text-[#A1A1AA] text-center mt-8">
            {mode === 'login' && "Don't have an account? "}
            {mode === 'register' && "Already have an account? "}
            {(mode === 'login' || mode === 'register') && (
              <button data-testid="auth-toggle-button" onClick={() => switchMode(mode === 'login' ? 'register' : 'login')} className="text-[#FF3B30] hover:text-[#FF645A] font-semibold transition-colors">
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
